// Copyright (c) 2022 by Move Closer

import { RawLocation } from 'vue-router'

import { RouteNames as OrderRoute } from '../../../orders/routes'
import { RouteNames as ProfileRoute } from '../../../profile/routes'
import { RouteNames as WishlistRoute } from '../../../wishlist/routes'

import { MobileMenuItemDefinition } from './Navbar.contracts'
import { PossibleAddon } from '../../../../modules/Hero/Hero.contracts'
import { AsyncComponent } from 'vue'
import { PossibleSnippets } from '../../../../support/snippet/config'

/**
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl>
 * @author Wojciech Falkowski <wojciech.falkowski@movecloser.pl>
 */
export interface StaticLink {

  action: (cb: any) => void
  /**
   * Id of link
   */
  id: string

  /**
   * Icon of the link
   */
  icon: string

  /**
   * Actual url where link is redirecting.
   */
  link: RawLocation
}

export enum StaticLinks {
  account = 'account',
  favouriteProducts = 'favouriteProducts',
  history = 'history',
  profile = 'profile'
}

/**
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl> (original)
 * @author Wojciech Falkowski <wojciech.falkowski@movecloser.pl> (edited)
 */
export const PossibleStaticLinks: Record<string, StaticLink> = {
  MyAccount: {
    id: StaticLinks.account.toString(),
    icon: 'SilhouetteIcon',
    link: { name: `orders.${OrderRoute.History}` },
    action: (cb) => {
      cb()
    }
  },
  FavouriteProducts: {
    id: StaticLinks.favouriteProducts.toString(),
    icon: 'HeartEmptyIcon',
    link: { name: `products.${WishlistRoute.Wishlist}` },
    action: (cb) => {
      cb()
    }
  }
}

/**
 * @author Filip Rurak <filip.rurak@movecloser.pl>
 */
export const possibleStaticLinksRegistry: Record<string, string> = {
  [StaticLinks.account]: `profile.${ProfileRoute.Account}`,
  [StaticLinks.history]: `orders.${OrderRoute.History}`
}

/**
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl> (original)
 */
export const PossibleMobileMenuItems: Record<string, MobileMenuItemDefinition> = {
  favourites: {
    badge: true,
    id: StaticLinks.favouriteProducts,
    label: 'favourites',
    icon: {
      default: 'HeartEmptyIcon',
      active: 'HeartFullIcon'
    }
  },
  menu: {
    id: 'menu',
    label: 'menu',
    icon: {
      default: 'HamburgerIcon',
      active: 'CloseIcon'
    }
  },
  home: {
    id: 'home',
    label: 'home',
    icon: {
      default: 'HouseLine',
      active: 'HouseLine'
    }
  },
  search: {
    id: 'search',
    label: 'search',
    icon: {
      default: 'SearchIcon',
      active: 'SearchIcon'
    }
  },
  account: {
    id: StaticLinks.account,
    label: 'account',
    icon: {
      default: 'SilhouetteIcon',
      active: 'SilhouetteIcon'
    }
  },
  cart: {
    badge: true,
    id: 'cart',
    label: 'cart',
    icon: {
      default: 'ShoppingCartIcon',
      active: 'ShoppingCartIcon'
    }
  },
  logo: {
    id: 'logo',
    label: 'logo',
    icon: {
      default: 'SimpleStoreLogo',
      active: 'SimpleStoreLogo'
    }
  },
  menuWithSearch: {
    id: 'menuWithSearch',
    label: 'menuWithSearch',
    icon: {
      default: 'SearchIcon',
      active: 'Close'
    }
  }
}

/**
 * TODO: Fill the config.
 *
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl>
 */
export const searchResultsConfig = {}

/**
 * @author Maciej Perzankowski <maciej.perzankowski@movecloser.pl>
 */
export const NAVBAR_COMPONENT_KEY = 'NavbarModule'

/**
 * @author Maciej Perzankowski <maciej.perzankowski@movecloser.pl>
 */
export enum NavbarTypes {
  DoubleNavAndLanguages = 'doubleNavAndLanguages',
  WithOpenSearch = 'withOpenSearch'
}

/**
 * @author Filip Rurak <filip.rurak2movecloser.pl>
 */
export const navbarMobileVersionsRegistry: Record<string, () => Promise<unknown>> = {
  [NavbarTypes.DoubleNavAndLanguages]: () => import(/* webpackChunkName: "shared" */ './partials/NavbarMobile/NavbarMobileSimple.vue'),
  [NavbarTypes.WithOpenSearch]: () => import(/* webpackChunkName: "shared" */ './partials/NavbarMobile/NavbarMobileSimple.vue')
}

/**
 * @author Filip Rurak <filip.rurak2movecloser.pl>
 */
export const navbarDesktopVersionsRegistry: Record<string, () => Promise<unknown>> = {
  [NavbarTypes.DoubleNavAndLanguages]: () => import(/* webpackChunkName: "shared" */ './partials/NavbarDesktop/NavbarDesktopSimple.vue'),
  [NavbarTypes.WithOpenSearch]: () => import(/* webpackChunkName: "shared" */ './partials/NavbarDesktop/NavbarDesktop.vue')
}

/**
 * @author Maciej Perzankowski <maciej.perzankowski@movecloser.pl>
 */
export const DEFAULT_STATIC_LINK_ICONS = [
  {
    id: 'account',
    order: 1,
    icon: 'SilhouetteIcon'
  },
  {
    id: 'favouriteProducts',
    order: 2,
    icon: 'HeartEmptyIcon'
  }
]

/**
 * @author Maciej Perzankowski <maciej.perzankowski@movecloser.pl>
 */
export const DEFAULT_MOBILE_NAV_ICONS = [
  {
    id: 'menu',
    label: 'menu',
    order: 1,
    icon: {
      default: 'HamburgerIcon',
      active: 'CloseIcon'
    }
  },
  {
    id: 'search',
    label: 'search',
    order: 2,
    icon: {
      default: 'SearchIcon',
      active: 'SearchIcon'
    }
  },
  {
    id: 'cart',
    label: 'cart',
    order: 3,
    icon: {
      default: 'ShoppingCartIcon',
      active: 'ShoppingCartIcon'
    }
  },
  {
    id: 'account',
    label: 'account',
    order: 4,
    icon: {
      default: 'SilhouetteIcon',
      active: 'SilhouetteIcon'
    }
  },
  {
    id: 'favourites',
    label: 'favourites',
    order: 5,
    icon: {
      default: 'HeartEmptyIcon',
      active: 'HeartFullIcon'
    }
  }
]

/**
 * @author Maciej Perzankowski <maciej.perzankowski@movecloser.pl>
 */
export const NAVBAR_WRAPPER_DEFAULT_CONFIG = {
  applyScrollBackground: false,
  staticLinkIcons: DEFAULT_STATIC_LINK_ICONS,
  showLanguageSwitcher: true,
  showDynamicResults: false,
  shouldSwitchLogoWhenPremium: false,
  possibleStaticLinks: {
    MyAccount: {
      link: 'history'
    }
  },
  shouldUseManyLogos: false
}

/**
 * @author Maciej Perzankowski <maciej.perzankowski@movecloser.pl>
 */
export const NAVBAR_MOBILE_DEFAULT_CONFIG = {
  type: 'slim',
  useDrawer: true,
  shouldHideMobileNav: false,
  mobileNavIcons: DEFAULT_MOBILE_NAV_ICONS
}

/**
 * @author Maciej Perzankowski <maciej.perzankowski@movecloser.pl>
 */
export const NAVBAR_DESKTOP_DEFAULT_CONFIG = {
  type: 'slim',
  showCartIconWithoutPrice: false,
  isNameVisible: false,
  loginStrategy: 'drawer',
  cartStrategy: 'drawer',
  menuOpeningStrategyOnTablet: 'text',
  possibleStaticLinks: {
    myAccount: {
      link: 'history'
    }
  },
  useSecondAuthDrawer: false
}

/**
 * @author Maciej Perzankowski <maciej.perzankowski@movecloser.pl>
 */
export const BODY_MARGIN_TOP_VAR = '--body-margin-top'
export const TOP_BAR_HEIGHT_VAR = '--topbar-height'
export const TOP_BAR_SELECTOR = '.TopBar'

export const navbarSnippetsRegistry: Record<PossibleAddon, AsyncComponent> = {
  [PossibleSnippets.CustomerSupport]: () => import(/* webpackChunkName: "shared/CustomerSupport" */ './addons/CustomerSupportAddon.vue')
}
